import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";
import SnapLayerHero from "@/components/layout/SnapLayerHero";
import { Loader2, KeyRound, CheckCircle2, Eye, EyeOff, Mail } from "lucide-react";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user has a valid session from the reset link
  useEffect(() => {
    // The hash fragment from the password reset email will be handled by Supabase
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // User came from password reset email, they can set new password
        console.log('Password recovery event detected');
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      setError(error.message);
      toast({ title: "Failed to reset password", description: error.message });
    } else {
      setSuccess(true);
      toast({ title: "Password updated", description: "Your password has been reset successfully" });
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-sl-bg text-sl-text flex flex-col">
        <SnapLayerHero
          title="Password Reset Complete"
          subtitle="Your password has been updated successfully."
        />

        <div className="container mx-auto px-4 py-12 flex-1 flex items-start justify-center">
          <div className="w-full max-w-md">
            <div className="bg-sl-surface border border-sl-surface-2 rounded-2xl p-8 shadow-lg text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold mb-4">All Set!</h2>
              <p className="text-sl-muted mb-6">
                Your password has been reset. You can now log in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-sl-accent hover:bg-sl-accent/90"
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-sl-bg text-sl-text flex flex-col">
      <SnapLayerHero
        title="Reset Your Password"
        subtitle="Enter a new password for your account."
      />

      <div className="container mx-auto px-4 py-12 flex-1 flex items-start justify-center">
        <div className="w-full max-w-md">
          <div className="bg-sl-surface border border-sl-surface-2 rounded-2xl p-8 shadow-lg">
            <div className="text-center mb-6">
              <div className="mx-auto w-14 h-14 bg-sl-accent/20 rounded-full flex items-center justify-center mb-4">
                <KeyRound className="h-7 w-7 text-sl-accent" />
              </div>
              <h1 className="text-2xl font-bold">New Password</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-slate-200">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password (8+ characters)"
                    required
                    className="bg-sl-bg border-sl-surface-2 text-white placeholder:text-slate-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-slate-200">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  className="bg-sl-bg border-sl-surface-2 text-white placeholder:text-slate-500"
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="w-full h-11 text-base font-semibold bg-sl-accent hover:bg-sl-accent/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>

            {/* Alternative options */}
            <div className="mt-6 pt-6 border-t border-sl-surface-2 space-y-3">
              <button
                type="button"
                onClick={() => navigate('/login?method=magic-link')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-sl-surface-2 bg-sl-surface-2 text-white hover:bg-sl-surface hover:border-sl-accent transition-colors font-medium"
              >
                <Mail className="h-4 w-4" />
                Send magic link instead
              </button>

              <p className="text-sm text-center text-slate-400">
                Remember your password?{" "}
                <Link
                  to="/login"
                  className="text-blue-400 hover:text-blue-300 hover:underline font-medium transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResetPassword;

